import globalPageStyle from '../css/globalPage.module.scss';
import { useEffect, useState, useRef } from 'react';
import styles from '../css/ordersPage.module.scss';
import axios from 'axios';
import { EnvPaths } from '../helpers/environment';
import { OrderObject } from '../helpers/objects/orderObject';
import { useAuth } from '../context/AuthContext';
import { createPortal } from 'react-dom';
import Pagination from '../components/pagination';
import tableStyles from '../css/table.module.scss';
import OrderDetailsModal from '../components/orderDetailsModal';
import { formatStatus } from '../helpers/functions';
import CustomDropdown from '../components/customDropdown';
import CustomSearchInput from '../components/customSearchInput';
import { useClickAway } from 'react-use';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';

const OrdersPage = () => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isActionOpen, setIsActionOpen] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [ordersList, setOrdersList] = useState<OrderObject[]>([]);
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [editOrder, setEditOrder] = useState<OrderObject | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const { user } = useAuth();
  const { t } = useTranslation();
  const [status, setStatus] = useState<{ value: string; label: string }>({ value: 'active', label: t('orders.labels.active') });
  const [filterStatusOpen, setFilterStatusOpen] = useState<boolean>(false);
  const [plan, setPlan] = useState<{ value: string; label: string }>({ value: 'all', label: t('orders.labels.all') });
  const [filterPlanOpen, setFilterPlanOpen] = useState<boolean>(false);
  const filterStatusDropRef = useRef<HTMLDivElement>(null);
  const planDropRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  const handleActionClick = (id: string, buttonElement: HTMLDivElement) => {
    if (buttonElement) {
      const rect = buttonElement.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.right - 205 + window.scrollX,
      });
    }
    setIsActionOpen(isActionOpen === id ? null : id);
  };

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${EnvPaths.SERVER_URL}/admin/get-orders?page=${page}&q=${searchTerm}&plan=${plan.value}&status=${status.value}`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      });
      const { orders, totalPages } = response.data;
      setOrdersList(orders);
      setTotalPages(totalPages);
      return orders;
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  useEffect(() => {
    setLoading(true);
    setTimeout(() => {
      if (user) {
        fetchOrders().then((response) => {
          setOrdersList(response ?? []);
          setLoading(false);
        });
      }
    }, 1000);
  }, [page, user, searchTerm, plan, status]);

  useEffect(() => {
    setStatus((prev) => ({ ...prev, label: t('orders.labels.active') }));
    setPlan((prev) => ({ ...prev, label: t('orders.labels.all') }));
  }, [t, i18next]);

  useClickAway(filterStatusDropRef, () => {
    setFilterStatusOpen(false);
  });

  const filterStatusOptions = [{ value: 'active', label: t('orders.labels.active') }];

  const handleStatusChange = (item: { value: string; label: string }) => {
    setStatus(item);
    setFilterStatusOpen(false);
  };

  useClickAway(planDropRef, () => {
    setFilterPlanOpen(false);
  });

  const filterPlanOptions = [
    { value: 'all', label: t('orders.labels.all') },
    { value: 'single', label: t('orders.labels.solo') },
    { value: 'family', label: t('orders.labels.family') },
  ];

  const handlePlanChange = (item: { value: string; label: string }) => {
    setPlan(item);
    setFilterPlanOpen(false);
  };

  return (
    <>
      {editOrder && <OrderDetailsModal order={editOrder} isOpen={editModalOpen} onClose={() => setEditModalOpen(false)} />}
      <main className={globalPageStyle.pageContainer}>
        <div className={globalPageStyle.pageContent}>
          <div className={styles.flexRow}>
            <CustomSearchInput onSearch={handleSearch} icon="/assets/images/search-icon.svg" placeholder={t('orders.search')} />
            <CustomDropdown ref={planDropRef} isOpen={filterPlanOpen} onToggle={() => setFilterPlanOpen(!filterPlanOpen)} options={filterPlanOptions} value={plan} onChange={handlePlanChange} width="215px" label={t('orders.plan')} />
            <CustomDropdown ref={filterStatusDropRef} isOpen={filterStatusOpen} onToggle={() => setFilterStatusOpen(!filterStatusOpen)} options={filterStatusOptions} value={status} onChange={handleStatusChange} width="195px" label={t('orders.status')} />
          </div>
          <br />
          {user && user?.role === 'admin' ? (
            loading ? (
              <div className="loader" />
            ) : ordersList.length > 0 ? (
              <div className={styles.body}>
                <div className={styles.ordersPageContainer}>
                  {/*Table*/}
                  <div className={tableStyles.tableContainer}>
                    <table className={`${tableStyles.table}`}>
                      <thead className={tableStyles.tableHeader}>
                        <tr>
                          <th>{t('orders.createdAt')}</th>
                          <th>{t('orders.plan')}</th>
                          <th>{t('orders.status')}</th>
                          <th>{t('orders.userEmail')}</th>
                          <th>{t('orders.skycopCode')}</th>
                          <th>{t('orders.expiringDate')}</th>
                          <th className={tableStyles.actionColumn}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {ordersList.map((order, index) => {
                          return (
                            <tr key={index}>
                              <td>
                                {order?.createdAt
                                  ? new Date(order.createdAt).toLocaleString('en-US', {
                                      year: 'numeric',
                                      month: 'numeric',
                                      day: 'numeric',
                                      hour: 'numeric',
                                      minute: 'numeric',
                                    })
                                  : ''}
                              </td>
                              <td>
                                {order?.plan?.product?.singleFlight ? t('orders.labels.single') : t('orders.labels.subscription')} {order?.plan?.product?.family ? t('orders.labels.family') : t('orders.labels.solo')} Plan
                              </td>
                              <td>{formatStatus(t, order?.status)}</td>
                              <td>{order?.unregisteredUserEmail}</td>
                              <td>{order?.skycopAssignedInfo?.code}</td>
                              <td>
                                {order?.skycopAssignedInfo?.expiringDate?.date
                                  ? new Date(order?.skycopAssignedInfo?.expiringDate?.date).toLocaleString('en-US', {
                                      year: 'numeric',
                                      month: 'numeric',
                                      day: 'numeric',
                                      hour: 'numeric',
                                      minute: 'numeric',
                                    })
                                  : ''}
                              </td>
                              <td className={tableStyles.actionColumn}>
                                <div className={tableStyles.actionShape} ref={buttonRef}>
                                  <img
                                    src="/assets/images/dots.svg"
                                    alt="dots"
                                    onClick={(e) => {
                                      const buttonElement = e.currentTarget as HTMLDivElement;
                                      handleActionClick(order._id, buttonElement);
                                    }}
                                  />
                                  {isActionOpen === order._id &&
                                    typeof window !== 'undefined' &&
                                    createPortal(
                                      <div
                                        className={`${tableStyles.actionShapeDropdown}`}
                                        style={{
                                          top: `${dropdownPosition.top}px`,
                                          left: `${dropdownPosition.left}px`,
                                        }}
                                      >
                                        <div
                                          onClick={() => {
                                            setEditOrder(order);
                                            setEditModalOpen(true);
                                            setIsActionOpen(null);
                                          }}
                                        >
                                          {t('orders.viewOrderDetails')}
                                        </div>
                                      </div>,
                                      document.body
                                    )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {/*Table*/}
                  {/*Pagination*/}
                  <Pagination onPrevious={() => setPage(page - 1)} onNext={() => setPage(page + 1)} currentPage={page} totalPages={totalPages} />
                  {/*Pagination*/}
                </div>
              </div>
            ) : (
              <h1>{t('orders.noOrders')}</h1>
            )
          ) : (
            <h1>{t('orders.noPermission')}</h1>
          )}
        </div>
      </main>
    </>
  );
};

export default OrdersPage;
